import { createContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { onSessionExpired } from '../services/authService';

export interface User {
  id?: number | string;
  email?: string;
  name?: string;
  role: string;
  client_id?: number | null;
  data?: any;
  [key: string]: any;
}

export interface AuthContextType {
  user: User | null;
  role: string | null;
  clientId: number | null;
  selectedClientId: number | null;
  setSelectedClientId: (id: number | null) => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  sessionExpired: boolean;
  setUser: (userData: User | null) => void;
  clearUser: () => void;
  refreshUser: () => void;
  clearSessionExpired: () => void;
}

const defaultContextValue: AuthContextType = {
  user: null,
  role: null,
  clientId: null,
  selectedClientId: null,
  setSelectedClientId: () => {},
  isAuthenticated: false,
  isLoading: true,
  sessionExpired: false,
  setUser: () => {},
  clearUser: () => {},
  refreshUser: () => {},
  clearSessionExpired: () => {}
};

export const AuthContext = createContext<AuthContextType>(defaultContextValue);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUserState] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [sessionExpired, setSessionExpired] = useState<boolean>(false);
  const [selectedClientId, setSelectedClientIdState] = useState<number | null>(() => {
    // Load from localStorage on mount
    if (typeof window !== 'undefined' && window.localStorage) {
      const stored = localStorage.getItem('adminSelectedClientId');
      if (stored) {
        const parsed = parseInt(stored, 10);
        return isNaN(parsed) ? null : parsed;
      }
    }
    return null;
  });

  // Subscribe to session expired events from authService interceptors
  useEffect(() => {
    const unsubscribe = onSessionExpired(() => {
      setSessionExpired(true);
    });
    return unsubscribe;
  }, []);

  const loadUserFromStorage = useCallback(() => {
    try {
      setIsLoading(true);

      if (typeof window === 'undefined' || !window.localStorage) {
        setUserState(null);
        setIsLoading(false);
        return;
      }

      const userStr = localStorage.getItem('user');
      if (userStr && userStr !== 'undefined') {
        const userData = JSON.parse(userStr) as User | { data: User };

        const actualUser = 'data' in userData ? userData.data : userData;

        setUserState(actualUser);
        setIsLoading(false);
        return;
      }

      const storedRole = localStorage.getItem('userRole');
      if (storedRole && storedRole !== 'undefined') {
        setUserState({ role: storedRole });
        setIsLoading(false);
        return;
      }

      setUserState(null);
      setIsLoading(false);
    } catch (error) {
      console.error('[AuthContext] Error loading user from storage:', error);
      setUserState(null);
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUserFromStorage();

    if (typeof window === 'undefined') return;

    const handleStorageChange = (e: StorageEvent): void => {
      if (e.key === 'user' || e.key === 'userRole' || e.key === 'token') {
        loadUserFromStorage();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [loadUserFromStorage]);

  const setUser = useCallback((userData: User | null) => {
    try {
      if (userData) {
        setUserState(userData);
        if (typeof window !== 'undefined' && window.localStorage) {
          localStorage.setItem('user', JSON.stringify(userData));
          if (userData.role) {
            localStorage.setItem('userRole', userData.role);
          }
        }
      } else {
        setUserState(null);
      }
    } catch (error) {
      console.error('[AuthContext] Error setting user:', error);
    }
  }, []);

  const clearUser = useCallback(() => {
    setUserState(null);
  }, []);

  const refreshUser = useCallback(() => {
    loadUserFromStorage();
  }, [loadUserFromStorage]);

  const clearSessionExpired = useCallback(() => {
    setSessionExpired(false);
  }, []);

  // 🔑 CLIENT CONTEXT: Allows admin_globale to select which client to operate as.
  // Persisted to localStorage so the axios interceptor can send x-client-context header.
  const setSelectedClientId = useCallback((id: number | null) => {
    setSelectedClientIdState(id);
    if (typeof window !== 'undefined' && window.localStorage) {
      if (id !== null) {
        localStorage.setItem('adminSelectedClientId', String(id));
      } else {
        localStorage.removeItem('adminSelectedClientId');
      }
    }
  }, []);

  const role = user?.role || null;
  const clientId = user?.client_id || null;
  // 🔒 SECURITY: Token is in httpOnly cookie, not accessible from JavaScript
  // Authentication is based on user object presence (non-sensitive data like name, email, role)
  const isAuthenticated = !!user;

  const value: AuthContextType = useMemo(() => ({
    user,
    role,
    clientId,
    selectedClientId,
    setSelectedClientId,
    isAuthenticated,
    isLoading,
    sessionExpired,
    setUser,
    clearUser,
    refreshUser,
    clearSessionExpired
  }), [
    user, role, clientId, selectedClientId, setSelectedClientId,
    isAuthenticated, isLoading, sessionExpired,
    setUser, clearUser, refreshUser, clearSessionExpired
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
